"use client";

import Link from "@/infrastructure/router/Link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invokeArtistRoute } from "@/infrastructure/supabase/functions";
import {
	ArtistIcon,
	EmptyState,
	GlassButton,
	GlassCard,
	NavigationBar,
	SheetHeader,
	Skeleton,
} from "@/components/design-system/ArtistDesignSystem";

/**
 * BillingMembershipPage — 开通会员页.
 *
 * Faithful port of `Features/Billing/BillingMembershipView.swift`. The iOS
 * screen is a full-bleed gradient surface split into three vertical zones:
 *   1. top bar (back / Enthusiast·Pro context switcher / 其他 + 灵感明细)
 *   2. hero (Standard / Premium / Max tier switcher, tier artwork, credit
 *      header, feature list)
 *   3. bottom purchase panel (Monthly + Yearly price cards, Subscribe CTA,
 *      agreement consent + paid-services / EULA footers)
 *
 * StoreKit2 is replaced by Supabase Edge Stripe Checkout. When the
 * billing backend is unreachable the page still renders prices and surfaces a
 * "Checkout unavailable" notice on tap, matching the iOS `unavailable` action.
 */

type BillingContext = "enthusiast" | "pro";
type BillingTier = "standard" | "premium" | "max";
type BillingCycle = "monthly" | "yearly";
type FeatureInfoId =
	| "watermark"
	| "imageModel"
	| "videoModel"
	| "creditRules"
	| "milkTea"
	| "standardExample"
	| "premiumExample"
	| "realTimeInspo"
	| "cancellationDetails"
	| "campaignAvailability";

type SheetState =
	| { readonly kind: "none" }
	| { readonly kind: "creditDetails" }
	| { readonly kind: "featureInfo"; readonly info: FeatureInfoId }
	| { readonly kind: "other" };

interface MembershipEntitlement {
	readonly active: boolean;
	readonly tier?: string;
	readonly renewsAt?: string;
	readonly credits?: number;
}

interface FeatureRowData {
	readonly title: string;
	readonly info?: FeatureInfoId;
}

const CONTEXT_LABEL: Readonly<Record<BillingContext, string>> = {
	enthusiast: "爱好者",
	pro: "专业",
};

const TIER_LABEL: Readonly<Record<BillingTier, string>> = {
	standard: "标准",
	premium: "高级",
	max: "Max",
};

const TIER_ORDER: ReadonlyArray<BillingTier> = ["standard", "premium", "max"];

/** Monthly credits per (context, tier). Mirrors `BillingPlanContext.fallbackMonthlyCredits`. */
const CREDITS: Readonly<
	Record<BillingContext, Readonly<Record<BillingTier, number>>>
> = {
	enthusiast: { standard: 100, premium: 170, max: 460 },
	pro: { standard: 1000, premium: 2550, max: 6650 },
};

/** Static display prices (CNY). On Web the authoritative source is Stripe. */
const DISPLAY_PRICE: Readonly<
	Record<BillingContext, Readonly<Record<BillingTier, string>>>
> = {
	enthusiast: { standard: "¥18", premium: "¥38", max: "¥98" },
	pro: { standard: "¥68", premium: "¥168", max: "¥328" },
};

const FEATURE_INFO_TITLE: Readonly<Record<FeatureInfoId, string>> = {
	watermark: "去除水印",
	imageModel: "Wan 2.7 Image Pro",
	videoModel: "Wan 2.7 R2V",
	creditRules: "灵感规则",
	milkTea: "奶茶",
	standardExample: "示例产出",
	premiumExample: "示例产出",
	realTimeInspo: "当前灵感额度",
	cancellationDetails: "取消订阅说明",
	campaignAvailability: "活动可用性",
};

const FEATURE_INFO_MESSAGE: Readonly<Record<FeatureInfoId, string>> = {
	watermark:
		"“AI 生成水印”指可见的品牌水印或文字型 “AI 生成” 标识，不包含可能存在的隐藏水印。",
	imageModel:
		"高级与 Max 包含更大参数的 Wan 2.7 Image Pro 路径，用于更高保真的图像生成。最终定价与可用性以后端为准。",
	videoModel:
		"Max 包含更大参数的 Wan 2.7 R2V 视频路径，用于参考到视频的创作。最终定价与可用性以后端为准。",
	creditRules:
		"订阅灵感按付费周期发放。活动资格内的发放保留其 3 个月有效期；其余订阅灵感在周期结束时过期。",
	milkTea: "“奶茶” 指国内常见的奶茶店饮品价位。",
	standardExample:
		"示例包含 6 段 720p 静音 Wan2.6-R2V-flash 视频、4 首含封面的 Nexo 歌曲、3 张 2K Wan2.7-Image 生成。实际定价可能调整，请以当前价格为准。",
	premiumExample:
		"示例包含 8 段 720p 静音 Wan2.6-R2V-flash 视频、4 首含封面与同步歌词的 Tempolor 歌曲、10 张 2K Wan2.7-Image 生成。实际定价可能调整，请以当前价格为准。",
	realTimeInspo: "请以订阅页展示的各套餐实时灵感额度为准。",
	cancellationDetails: "请以订阅页展示的取消订阅说明为准。",
	campaignAvailability: "限时活动可能不定期开放。",
};

const FEATURES: Readonly<
	Record<
		BillingContext,
		Readonly<Record<BillingTier, ReadonlyArray<FeatureRowData>>>
	>
> = {
	enthusiast: {
		standard: [
			{ title: "以一杯奶茶的价格开启你的第一次 AI 创作", info: "milkTea" },
			{ title: "AI 生成水印？这里没有", info: "watermark" },
			{ title: "图像、音乐、视频共用一个灵感池，不再被锁定在单一内容类型" },
			{
				title:
					"示例产出：6 段 720p 静音视频 + 4 首 Nexo 歌曲 + 3 张 2K 图像 —— 足以完成一整套 Artist 风格歌曲包",
				info: "standardExample",
			},
			{ title: "相对其他平台明显更低的入门价格" },
		],
		premium: [
			{ title: "任何生成均无品牌水印", info: "watermark" },
			{
				title: "约为标准版 2 倍的灵感，让你尝试更多创意",
				info: "realTimeInspo",
			},
			{ title: "按月订阅，灵活取消", info: "cancellationDetails" },
			{
				title:
					"示例产出：8 段 720p 静音视频 + 4 首 Tempolor 歌曲 + 10 张 2K 图像 —— 更轻松完成完整歌曲包",
				info: "premiumExample",
			},
			{ title: "依旧低于其他平台入门方案的价格" },
		],
		max: [
			{ title: "任何生成均无品牌水印", info: "watermark" },
			{ title: "爱好者方案中最高的灵感额度", info: "realTimeInspo" },
			{ title: "灵感有效期延长至 3 个月", info: "campaignAvailability" },
			{ title: "约等于多数平台的标准价位" },
			{ title: "不限于单一内容类型 —— 图像、音乐、视频皆可创作" },
			{ title: "面向高频创作；不建议大量使用重型模型或 1080p 视频" },
		],
	},
	pro: {
		standard: [
			{ title: "为独立音乐人、Remixer、创作者提供稳定的月度预算" },
			{ title: "频繁的 Nexo 音乐生成与图像刷新" },
			{ title: "发布歌曲、封面与轻量视觉内容" },
			{ title: "在移动端创建、发布、分享 Artist Continents" },
			{ title: "清晰的月度预算，便于音乐 / 图像 / 视觉实验" },
			{ title: "任何生成均无品牌水印", info: "watermark" },
		],
		premium: [
			{ title: "构建完整的发行、营销素材与视觉方向" },
			{ title: "音乐、图像、视频概念可并行周迭代" },
			{
				title: "全规格 Wan 2.7 Image Pro，更高保真图像生成",
				info: "imageModel",
			},
			{ title: "为 Wan 视频、歌词与重生成留出更多额度" },
			{
				title: "在符合条件的滚动促销期间享受更长的灵感有效期",
				info: "creditRules",
			},
			{ title: "任何生成均无品牌水印", info: "watermark" },
		],
		max: [
			{ title: "面向高强度创作者、工作室与发行团队" },
			{ title: "把每首歌变成一个完整的视觉世界" },
			{ title: "全规格 Wan 2.7 R2V，参考到视频生成", info: "videoModel" },
			{ title: "重型视频、Image Pro、音乐视觉与重生成工作流" },
			{
				title: "在符合条件的滚动促销期间享受更长的灵感有效期",
				info: "creditRules",
			},
			{ title: "任何生成均无品牌水印", info: "watermark" },
		],
	},
};

/** Background gradient per (context, tier). Mirrors `BillingPlanContext.backgroundGradient(for:)`. */
const BACKGROUND_GRADIENT: Readonly<
	Record<BillingContext, Readonly<Record<BillingTier, string>>>
> = {
	enthusiast: {
		standard: "linear-gradient(135deg, rgb(64 159 230), rgb(89 194 242))",
		premium: "linear-gradient(135deg, rgb(10 56 209), rgb(59 120 255))",
		max: "linear-gradient(160deg, rgb(5 13 97), rgb(10 51 199), rgb(87 199 250))",
	},
	pro: {
		standard: "linear-gradient(135deg, rgb(46 51 56), rgb(87 92 97))",
		premium:
			"linear-gradient(135deg, rgb(18 20 26), rgb(51 54 61), rgb(97 100 107))",
		max: "linear-gradient(160deg, rgb(3 3 4), rgb(26 27 31), rgb(82 84 92))",
	},
};

/** Purchase panel style per context. Mirrors `BillingPurchasePanelStyle`. */
interface PurchasePanelStyle {
	readonly panelBackground: string;
	readonly selectedCardBackground: string;
	readonly disabledCardBackground: string;
	readonly selectedCardStroke: string;
	readonly primaryText: string;
	readonly secondaryText: string;
	readonly footerPrimary: string;
	readonly footerSecondary: string;
	readonly buttonTint: string;
}

const PURCHASE_PANEL_STYLE: Readonly<
	Record<BillingContext, PurchasePanelStyle>
> = {
	enthusiast: {
		panelBackground: "#ffffff",
		selectedCardBackground: "#ffffff",
		disabledCardBackground: "rgb(240 242 247)",
		selectedCardStroke: "rgb(17 17 22)",
		primaryText: "rgb(17 17 22)",
		secondaryText: "rgba(17 17 22, 0.55)",
		footerPrimary: "rgba(17 17 22, 0.72)",
		footerSecondary: "rgba(17 17 22, 0.5)",
		buttonTint: "rgb(17 17 22)",
	},
	pro: {
		panelBackground: "rgb(14 15 17)",
		selectedCardBackground: "rgb(26 27 31)",
		disabledCardBackground: "rgb(36 37 41)",
		selectedCardStroke: "rgba(255 255 255, 0.86)",
		primaryText: "#ffffff",
		secondaryText: "rgba(255 255 255, 0.6)",
		footerPrimary: "rgba(255 255 255, 0.74)",
		footerSecondary: "rgba(255 255 255, 0.52)",
		buttonTint: "#ffffff",
	},
};

const SYSTEM_URLS = {
	paidServices: "https://artist.app/paid-services",
	privacyPolicy: "https://artist.app/privacy",
	termsOfService: "https://artist.app/terms",
	eula: "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/",
} as const;

const AGREEMENT_PREFS_KEY = "artist.preferences.billing.v1";

export function BillingMembershipPage() {
	const [context, setContext] = useState<BillingContext>("enthusiast");
	const [tier, setTier] = useState<BillingTier>("standard");
	const [cycle, setCycle] = useState<BillingCycle>("monthly");
	const [sheet, setSheet] = useState<SheetState>({ kind: "none" });
	const [entitlement, setEntitlement] = useState<MembershipEntitlement | null>(
		null,
	);
	const [entitlementLoading, setEntitlementLoading] = useState(true);
	const [busy, setBusy] = useState<"checkout" | "restore" | "portal" | null>(
		null,
	);
	const [notice, setNotice] = useState<{
		readonly tone: "info" | "error" | "success";
		readonly text: string;
	} | null>(null);
	const [agreementAccepted, setAgreementAccepted] = useState(false);

	const refreshEntitlement = useCallback(async (): Promise<void> => {
		setEntitlementLoading(true);
		try {
			setEntitlement(
				await invokeArtistRoute<MembershipEntitlement>("/billing", {
					body: JSON.stringify({ action: "entitlement" }),
				}),
			);
		} catch {
			setEntitlement({ active: false });
		} finally {
			setEntitlementLoading(false);
		}
	}, []);

	useEffect(() => {
		try {
			const raw = window.localStorage.getItem(AGREEMENT_PREFS_KEY);
			if (raw === "1") setAgreementAccepted(true);
		} catch {
			/* localStorage may be unavailable; defaults remain. */
		}
		void refreshEntitlement();
	}, [refreshEntitlement]);

	function persistAgreement(accepted: boolean): void {
		setAgreementAccepted(accepted);
		try {
			window.localStorage.setItem(AGREEMENT_PREFS_KEY, accepted ? "1" : "0");
		} catch {
			/* localStorage may be unavailable; state still updates in-memory. */
		}
	}

	const cta = useMemo(
		() => resolveCallToAction({ entitlement, tier, context }),
		[entitlement, tier, context],
	);

	const selectedCredits = CREDITS[context][tier];
	const selectedPrice = DISPLAY_PRICE[context][tier];
	const panelStyle = PURCHASE_PANEL_STYLE[context];

	const perform = useCallback(
		async (action: "checkout" | "restore" | "portal"): Promise<void> => {
			if (!agreementAccepted && action === "checkout") {
				setNotice({ tone: "info", text: "请先阅读并同意相关协议。" });
				return;
			}
			setBusy(action);
			setNotice(null);
			try {
				const result = await invokeArtistRoute<{
					readonly url?: string;
					readonly message?: string;
					readonly active?: boolean;
				}>("/billing", {
					body: JSON.stringify({
						action,
						tier,
						audience: context,
						cycle,
						returnUrl:
							typeof window === "undefined" ? undefined : window.location.href,
					}),
				});
				if (typeof result.url === "string" && result.url.length > 0) {
					if (typeof window !== "undefined") window.location.assign(result.url);
					return;
				}
				await refreshEntitlement();
				setNotice({
					tone: "success",
					text:
						action === "restore"
							? "购买记录已恢复。"
							: action === "portal"
								? "已打开订阅管理。"
								: "会员信息已更新。",
				});
			} catch {
				setNotice({
					tone: "error",
					text:
						action === "checkout"
							? "Checkout unavailable — 暂未接入支付，请稍后重试。"
							: action === "restore"
								? "恢复失败，请稍后重试。"
								: "暂时无法完成，请稍后重试。",
				});
			} finally {
				setBusy(null);
			}
		},
		[agreementAccepted, context, cycle, tier, refreshEntitlement],
	);

	const background = BACKGROUND_GRADIENT[context][tier];
	return (
		<section
			className="billing-page"
			aria-label="开通会员"
			style={{ background }}
		>
			<NavigationBar
				leading={
					<Link className="ds-icon-button" href="/settings" aria-label="返回">
						<ArtistIcon name="back" size={28} strokeWidth={2.2} />
					</Link>
				}
				title={<ContextSelector value={context} onChange={setContext} />}
				trailing={
					<button
						type="button"
						className="ds-icon-button"
						onClick={() => setSheet({ kind: "other" })}
						aria-label="其他会员操作"
					>
						<ArtistIcon name="more" size={20} />
					</button>
				}
				className="billing-topbar"
			/>

			{notice ? (
				<div
					className="billing-toast"
					role={notice.tone === "error" ? "alert" : "status"}
				>
					{notice.text}
				</div>
			) : null}

			<main className="billing-hero">
				<TierSelector value={tier} onChange={setTier} />

				<button
					type="button"
					className="billing-credit-header"
					onClick={() => setSheet({ kind: "creditDetails" })}
					aria-label="查看灵感明细"
				>
					<span className="billing-credit-count">
						<ArtistIcon name="sparkle" size={19} />
						<strong>{selectedCredits.toLocaleString()}</strong>
						<em>灵感/月</em>
					</span>
					<p>同一灵感池可用于图像、视频、音乐与歌词生成。</p>
				</button>

				<FeatureList
					rows={FEATURES[context][tier]}
					onShowInfo={(info) => setSheet({ kind: "featureInfo", info })}
				/>

				{entitlementLoading ? (
				// biome-ignore lint/a11y/useAriaPropsSupportedByRole: div has no implicit role; aria-label conveys loading status to assistive tech on a decorative skeleton container
				<div
					className="billing-entitlement-skeleton"
					aria-label="正在加载会员状态"
				>
					<Skeleton rows={1} />
				</div>
				) : entitlement?.active ? (
					<GlassCard className="billing-entitlement">
						<ArtistIcon name="crown" size={24} />
						<div>
							<strong>{entitlement.tier ?? "Artist"} 会员已生效</strong>
							<span>
								{entitlement.credits?.toLocaleString() ?? "—"} 灵感
								{entitlement.renewsAt ? ` · ${entitlement.renewsAt} 续订` : ""}
							</span>
						</div>
						<GlassButton
							size="sm"
							onClick={() => void perform("portal")}
							disabled={busy !== null}
						>
							{busy === "portal" ? "正在打开…" : "管理订阅"}
						</GlassButton>
					</GlassCard>
				) : null}
			</main>

			<footer
				className="billing-purchase-panel"
				style={{
					background: panelStyle.panelBackground,
					color: panelStyle.primaryText,
				}}
			>
				<div
					className="billing-price-picker"
					role="radiogroup"
					aria-label="计费周期"
				>
					<PriceOptionCard
						title="包月"
						subtitle="通过 App Store / Web 支付每月自动续订。"
						price={selectedPrice}
						isSelected={cycle === "monthly"}
						isEnabled
						style={panelStyle}
						onSelect={() => setCycle("monthly")}
					/>
					<PriceOptionCard
						title="包年"
						subtitle="包年方案暂未开放，敬请期待。"
						price="即将推出"
						isSelected={cycle === "yearly"}
						isEnabled={false}
						style={panelStyle}
						onSelect={() => setCycle("yearly")}
					/>
				</div>

				<GlassButton
					kind="primary"
					size="lg"
					expands
					isEnabled={cta.enabled && busy === null}
					isLoading={busy === "checkout"}
					onClick={() => void perform("checkout")}
					className="billing-subscribe-cta"
				>
					{busy === "checkout" ? "正在前往安全支付…" : cta.label}
				</GlassButton>

				<PurchaseFooter
					agreementAccepted={agreementAccepted}
					onAgreementChange={persistAgreement}
					style={panelStyle}
				/>
			</footer>

			<BillingSheet
				sheet={sheet}
				onClose={() => setSheet({ kind: "none" })}
				entitlement={entitlement}
			/>
		</section>
	);
}

interface ContextSelectorProps {
	readonly value: BillingContext;
	readonly onChange: (next: BillingContext) => void;
}

function ContextSelector({ value, onChange }: Readonly<ContextSelectorProps>) {
	return (
		<div
			className="billing-context-selector"
			role="tablist"
			aria-label="方案类型"
		>
			{(["enthusiast", "pro"] as const).map((candidate, index) => (
				<button
					key={candidate}
					type="button"
					role="tab"
					aria-selected={value === candidate}
					className={`billing-context-button${value === candidate ? " billing-context-button--selected" : ""}`}
					onClick={() => onChange(candidate)}
				>
					<span>{CONTEXT_LABEL[candidate]}</span>
					{value === candidate ? (
						<span className="billing-context-underline" aria-hidden="true" />
					) : null}
					{index === 0 ? (
						<span className="billing-context-slash" aria-hidden="true">
							/
						</span>
					) : null}
				</button>
			))}
		</div>
	);
}

interface TierSelectorProps {
	readonly value: BillingTier;
	readonly onChange: (next: BillingTier) => void;
}

function TierSelector({ value, onChange }: Readonly<TierSelectorProps>) {
	return (
		<div className="billing-tier-selector" role="tablist" aria-label="会员等级">
			{TIER_ORDER.map((candidate) => (
				<button
					key={candidate}
					type="button"
					role="tab"
					aria-selected={value === candidate}
					className={`billing-tier-pill${value === candidate ? " billing-tier-pill--selected" : ""}`}
					onClick={() => onChange(candidate)}
				>
					{TIER_LABEL[candidate]}
				</button>
			))}
		</div>
	);
}

interface FeatureListProps {
	readonly rows: ReadonlyArray<FeatureRowData>;
	readonly onShowInfo: (info: FeatureInfoId) => void;
}

function FeatureList({ rows, onShowInfo }: Readonly<FeatureListProps>) {
	return (
		<ul className="billing-feature-list">
			{rows.map((row) => (
				<li key={row.title} className="billing-feature-row">
					<ArtistIcon
						name="check"
						size={14}
						className="billing-feature-check"
					/>
					<span>{row.title}</span>
					{row.info === undefined ? null : (
						<FeatureInfoButton info={row.info} onShowInfo={onShowInfo} />
					)}
				</li>
			))}
		</ul>
	);
}

interface FeatureInfoButtonProps {
	readonly info: FeatureInfoId;
	readonly onShowInfo: (info: FeatureInfoId) => void;
}

function FeatureInfoButton({
	info,
	onShowInfo,
}: Readonly<FeatureInfoButtonProps>) {
	return (
		<button
			type="button"
			className="billing-feature-info"
			aria-label={FEATURE_INFO_TITLE[info]}
			onClick={() => onShowInfo(info)}
		>
			<ArtistIcon name="info" size={13} />
		</button>
	);
}

interface PriceOptionCardProps {
	readonly title: string;
	readonly subtitle: string;
	readonly price: string;
	readonly isSelected: boolean;
	readonly isEnabled: boolean;
	readonly style: PurchasePanelStyle;
	readonly onSelect: () => void;
}

function PriceOptionCard({
	title,
	subtitle,
	price,
	isSelected,
	isEnabled,
	style,
	onSelect,
}: Readonly<PriceOptionCardProps>) {
	return (
		// biome-ignore lint/a11y/useSemanticElements: price card needs button interactivity + custom styled card surface; native radio input cannot host the rich card layout
		<button
			type="button"
			role="radio"
			aria-checked={isSelected}
			aria-disabled={!isEnabled}
			className={`billing-price-card${isSelected ? " billing-price-card--selected" : ""}${isEnabled ? "" : " billing-price-card--disabled"}`}
			onClick={onSelect}
			disabled={!isEnabled}
			style={{
				background: isSelected
					? style.selectedCardBackground
					: style.disabledCardBackground,
				borderColor: isSelected ? style.selectedCardStroke : "transparent",
				color: style.primaryText,
				opacity: isEnabled ? 1 : 0.62,
			}}
		>
			<span className="billing-price-card__copy">
				<strong>{title}</strong>
				<small style={{ color: style.secondaryText }}>{subtitle}</small>
			</span>
			<span className="billing-price-card__price">{price}</span>
		</button>
	);
}

interface PurchaseFooterProps {
	readonly agreementAccepted: boolean;
	readonly onAgreementChange: (next: boolean) => void;
	readonly style: PurchasePanelStyle;
}

function PurchaseFooter({
	agreementAccepted,
	onAgreementChange,
	style,
}: Readonly<PurchaseFooterProps>) {
	return (
		<div
			className="billing-purchase-footer"
			style={{ color: style.footerPrimary }}
		>
			<label className="billing-agreement">
				<input
					type="checkbox"
					checked={agreementAccepted}
					onChange={(event) => onAgreementChange(event.currentTarget.checked)}
				/>
				<ArtistIcon
					name={agreementAccepted ? "check" : "close"}
					size={13}
					className="billing-agreement-icon"
				/>
				<span>我已阅读并同意</span>
				<a
					href={SYSTEM_URLS.paidServices}
					target="_blank"
					rel="noreferrer"
					style={{ color: style.footerPrimary }}
				>
					Artist 付费服务协议（含自动续订条款）
				</a>
			</label>
			<p
				className="billing-purchase-notice"
				style={{ color: style.footerSecondary }}
			>
				购买后请在账户设置中关闭自动续订
				<span aria-hidden="true"> | </span>
				<a
					href={SYSTEM_URLS.eula}
					target="_blank"
					rel="noreferrer"
					style={{ color: style.footerSecondary }}
				>
					EULA
				</a>
			</p>
		</div>
	);
}

interface BillingSheetProps {
	readonly sheet: SheetState;
	readonly onClose: () => void;
	readonly entitlement: MembershipEntitlement | null;
}

function BillingSheet({
	sheet,
	onClose,
	entitlement,
}: Readonly<BillingSheetProps>) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const node = dialogRef.current;
		if (node === null) return;
		if (sheet.kind === "none") {
			if (node.open) node.close();
			return;
		}
		if (!node.open) node.showModal();
	}, [sheet]);

	return (
		<dialog ref={dialogRef} className="billing-sheet" onClose={onClose}>
			{sheet.kind === "creditDetails" ? (
				<CreditDetailSheet onClose={onClose} entitlement={entitlement} />
			) : null}
			{sheet.kind === "featureInfo" ? (
				<FeatureInfoSheet info={sheet.info} onClose={onClose} />
			) : null}
			{sheet.kind === "other" ? <OtherSheet onClose={onClose} /> : null}
		</dialog>
	);
}

interface CreditDetailSheetProps {
	readonly onClose: () => void;
	readonly entitlement: MembershipEntitlement | null;
}

function CreditDetailSheet({
	onClose,
	entitlement,
}: Readonly<CreditDetailSheetProps>) {
	const credits = entitlement?.credits ?? 0;
	return (
		<div className="billing-credit-sheet">
			<SheetHeader
				title="我的灵感"
				leading="cancel"
				leadingLabel="关闭"
				onLeading={onClose}
			/>
			<div className="billing-credit-sheet__body">
				<strong className="billing-credit-sheet__total">
					{credits.toLocaleString()}
				</strong>
				<p>订阅灵感 · 购买灵感 · 赠送灵感</p>
				<p className="billing-credit-sheet__notice">
					仅展示近期灵感数据，更新可能延迟。
				</p>
				{credits === 0 ? (
					<EmptyState
						title="暂无灵感记录"
						message="开通会员后灵感将出现在此处。"
					/>
				) : (
					<ul className="billing-credit-records">
						<li>
							<div>
								<strong>订阅灵感</strong>
								<span>本次周期发放</span>
							</div>
							<span className="billing-credit-amount billing-credit-amount--income">
								+{credits.toLocaleString()}
							</span>
						</li>
					</ul>
				)}
			</div>
		</div>
	);
}

interface FeatureInfoSheetProps {
	readonly info: FeatureInfoId;
	readonly onClose: () => void;
}

function FeatureInfoSheet({ info, onClose }: Readonly<FeatureInfoSheetProps>) {
	return (
		<div className="billing-feature-info-sheet">
			<SheetHeader
				title={FEATURE_INFO_TITLE[info]}
				leading="cancel"
				leadingLabel="关闭"
				onLeading={onClose}
			/>
			<div className="billing-feature-info-sheet__body">
				<p>{FEATURE_INFO_MESSAGE[info]}</p>
				<GlassButton kind="primary" size="md" expands onClick={onClose}>
					知道了
				</GlassButton>
			</div>
		</div>
	);
}

interface OtherSheetProps {
	readonly onClose: () => void;
}

function OtherSheet({ onClose }: Readonly<OtherSheetProps>) {
	return (
		<div className="billing-other-sheet">
			<SheetHeader
				title="其他"
				leading="cancel"
				leadingLabel="关闭"
				onLeading={onClose}
			/>
			<div className="billing-other-sheet__body">
				<section>
					<h2>App Store</h2>
					<ul className="billing-other-list">
						<li>
							<span>恢复购买</span>
							<ArtistIcon name="chevron" size={14} />
						</li>
						<li>
							<span>管理订阅</span>
							<ArtistIcon name="chevron" size={14} />
						</li>
					</ul>
				</section>
				<section>
					<h2>法律条款</h2>
					<ul className="billing-other-list">
						<LegalRow
							label="Artist 付费服务协议"
							href={SYSTEM_URLS.paidServices}
						/>
						<LegalRow label="隐私政策" href={SYSTEM_URLS.privacyPolicy} />
						<LegalRow label="服务条款" href={SYSTEM_URLS.termsOfService} />
						<LegalRow label="Apple 标准 EULA" href={SYSTEM_URLS.eula} />
					</ul>
				</section>
			</div>
		</div>
	);
}

function LegalRow({ label, href }: Readonly<{ label: string; href: string }>) {
	return (
		<li>
			<a href={href} target="_blank" rel="noreferrer">
				<span>{label}</span>
			</a>
			<ArtistIcon name="chevron" size={14} />
		</li>
	);
}

interface CallToActionInput {
	readonly entitlement: MembershipEntitlement | null;
	readonly tier: BillingTier;
	readonly context: BillingContext;
}

interface CallToAction {
	readonly label: string;
	readonly enabled: boolean;
}

function resolveCallToAction({
	entitlement,
	tier,
}: Readonly<CallToActionInput>): CallToAction {
	if (entitlement?.active && entitlement.tier === tier) {
		return { label: "当前方案已生效", enabled: false };
	}
	if (entitlement?.active) {
		return { label: "立即升级", enabled: true };
	}
	return { label: `订阅 ${TIER_LABEL[tier]} 会员`, enabled: true };
}
